import { select, takeLatest } from 'typed-redux-saga';

import { Currency } from '@/domains/core/localization/interfaces/Currency';
import { getCurrency, isB2B } from '@/domains/core/settings/utils';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { customerProductPreferencesSelector } from '@/domains/customerManagement/customerProductPreferences/selectors';
import { TrackedProduct } from '@/domains/shoppingCart/common/interfaces/TrackedProduct';
import { type TrackedProductDetails } from '@/domains/shoppingCart/common/interfaces/TrackedProductDetails';
import {
  ADD_TO_CART_SUCCESS,
  SHOW_ADD_T0_CART_MODAL,
  type AddToCartSuccessAction,
  type PlaylistReconciliation,
} from '@/shoppingCart/AddToCart/AddToCart.actions';
import { selectAddToCartState } from '@/shoppingCart/AddToCart/selectors/selectAddToCartState';
import { selectModalVisible } from '@/shoppingCart/AddToCart/selectors/selectModalVisible';
import { selectSavedProducts } from '@/shoppingCart/AddToCart/selectors/selectSavedProducts';
import { toTrackedProduct } from '@/shoppingCart/common/utils/toTrackedProduct';
import { getTrackingDate } from '@/shoppingCart/tracking/utils';

export type AddToCartProductAdditionsGtmEvent = {
  event: 'interaction_add to cart';
  event_trigger: string;
  e_category: 'ecommerce';
  e_action: 'add to cart';
  e_label: string;
  amp_event_name: 'Add to cart';
  date: string;
  currency: Currency;
  is_new_customer: undefined;
  playlist_fingerprint?: string;
  playlist_reconciliation?: string;
  mf_product_count: number;
  pro_product_count: number;
  block_clicked?: string;
  playlist_name: string;
  listing_fingerprint: undefined;
  playlist_article_ids: undefined;
  playlist_model_ids: undefined;
  playlist_category_ids: undefined;
  playlist_product_ids: undefined;
  playlist_b2b_product_count: undefined;
  playlist_content_id: undefined;
  playlist_position: number;
  has_a_favorite_product: boolean;
  has_non_deductible_tva_disclaimer?: boolean;
  pr_products: Array<TrackedProduct>;
};

const createAddToCartProductAdditionsGtmEvent = (
  products: TrackedProductDetails[],
  playlistReconciliation?: PlaylistReconciliation,
  favoriteProducts?: Set<number>,
): AddToCartProductAdditionsGtmEvent => {
  const playlistName =
    playlistReconciliation?.playlistName?.titleLabel ||
    playlistReconciliation?.playlistName?.blockClicked ||
    '';
  return {
    event: 'interaction_add to cart',
    event_trigger: products ? products[0]?.event_trigger || '' : '',
    e_category: 'ecommerce',
    e_action: 'add to cart',
    e_label: playlistName,
    amp_event_name: 'Add to cart',
    date: getTrackingDate(),
    currency: getCurrency(),
    is_new_customer: undefined,
    playlist_fingerprint: playlistReconciliation?.playlistFingerprint,
    playlist_reconciliation: playlistReconciliation?.playlistReconciliation,
    mf_product_count: products.filter((product) => product.pr_is_mf).length,
    pro_product_count: products.filter((product) => product.b2b).length,
    block_clicked: playlistReconciliation?.blockClicked,
    playlist_name: playlistName,
    listing_fingerprint: undefined,
    playlist_article_ids: undefined,
    playlist_model_ids: undefined,
    playlist_category_ids: undefined,
    playlist_product_ids: undefined,
    playlist_b2b_product_count: undefined,
    playlist_content_id: undefined,
    playlist_position: playlistReconciliation?.playlistPosition || 0,
    has_a_favorite_product: products.some((product) =>
      favoriteProducts?.has(Number(product.pr_model_id)),
    ),
    has_non_deductible_tva_disclaimer: isB2B()
      ? products?.[0]?.foreignSeller
      : undefined,
    pr_products: products.map((product) =>
      toTrackedProduct({ product, playlistReconciliation }),
    ),
  };
};

function* trackAddToCartProductAdditions(action: AddToCartSuccessAction) {
  const { products, savedProductIds } = action.payload;

  const addToCartState = yield* select(selectAddToCartState);

  const { purchasedTwice: favoriteProducts } = yield* select(
    customerProductPreferencesSelector,
  );

  const addedProducts = products.filter((product: TrackedProductDetails) =>
    savedProductIds.includes(product.id),
  );

  if (addedProducts.length > 0) {
    const gtmEvent = createAddToCartProductAdditionsGtmEvent(
      addedProducts,
      addToCartState.playlistReconciliation,
      favoriteProducts,
    );
    Gtm.pushEvent(gtmEvent);
  }
}

function* trackAddToCartModelShow() {
  const isModalVisible = yield* select(selectModalVisible);
  if (isModalVisible) {
    const addedProductIds = yield* select(selectSavedProducts);
    const productsHaveBeenAdded = addedProductIds.length > 0;
    const gtmEvent = {
      event: 'interaction_display add to cart modal',
      e_category: 'navigation',
      e_action: 'Display add to cart modal',
      event_trigger: productsHaveBeenAdded
        ? 'Added to cart'
        : 'Product out of stock',
      amp_event_name: 'Display add to cart modal',
    };
    Gtm.push(gtmEvent);
  }
}

export function* watchAddToCartTrackingSaga() {
  yield* takeLatest(ADD_TO_CART_SUCCESS, trackAddToCartProductAdditions);
}

export function* watchAddToCartModelTrackingSaga() {
  yield* takeLatest([SHOW_ADD_T0_CART_MODAL], trackAddToCartModelShow);
}
